// MIRRORED FILE — twin of src/utils/clientInvoiceLine.js (the billed-line readings,
// priceClientInvoiceLine, lineMarkup, isPersonSetCost and the D170.7 Not supplied readings; line-for-line). CRA's ModuleScopePlugin forbids importing from the repo's
// src/, so the copy is the only way the page can use the rule. The pair is registered in
// `mirroredUtilParity.test.js` (PAIRS). The server's zeroUnbilledLineTotal and line-number helpers stay server-only.
//
// What ONE client-invoice line billed: how many, for how much, at what price. A line does not
// store its unit price - `invoiceLineTotal` and `invoiceQuantity` are the facts. Missing is null,
// never 0 (a stated 0 is a rate and bills 0.00).
import { num } from './invoiceLineMath';
import { ceilPrice } from './priceUtils';
import { sellingPriceAt, realizedMarkupPercent } from './pricing';

const round2 = (n: number): number => Number(n.toFixed(2));

/** How many units this line billed: `invoiceQuantity`, else `totalQtyBilled` (lines older than the sync). */
export function billedQuantity(item: any): number | null {
  const qty = num(item?.invoiceQuantity);
  if (qty !== null) return qty;
  return num(item?.totalQtyBilled);
}

/** The money this line billed, before VAT. Null when the line states none. */
export function billedTotal(item: any): number | null {
  return num(item?.invoiceLineTotal);
}

/** total / quantity to the cent; null when either is missing or the quantity is not positive. */
export function billedUnitPrice(item: any): number | null {
  const qty = billedQuantity(item);
  const total = billedTotal(item);
  if (qty === null || total === null || qty <= 0) return null;
  return Number((total / qty).toFixed(2));
}

/** The money of ONE line from its quantity and unit RATES: rates extended and ceiled once, never from a rounded total. */
export function priceClientInvoiceLine({
  quantity,
  quoteUnitPrice,
  actualUnitCost,
  quoteLineTotal = null,
}: {
  quantity: any;
  quoteUnitPrice: any;
  actualUnitCost: any;
  quoteLineTotal?: any;
}): {
  invoiceUnitPrice: number | null;
  invoiceLineTotal: number | null;
  actualLineCost: number | null;
  markupAmount: number | null;
  markupPercent: number | null;
  varianceAmount: number | null;
  variancePercent: number | null;
} {
  const qty = Math.max(0, num(quantity) ?? 0); // D153.9: billed less than 0 bills nothing, as 0 does
  const quote = num(quoteUnitPrice);
  const cost = num(actualUnitCost);
  const quoted = num(quoteLineTotal);

  const invoiceUnitPrice = quote;
  const invoiceLineTotal = invoiceUnitPrice == null ? null : ceilPrice(invoiceUnitPrice * qty);
  const actualLineCost = cost == null ? null : ceilPrice(cost * qty);
  const markupAmount = invoiceLineTotal == null || actualLineCost == null ? null : round2(invoiceLineTotal - actualLineCost);
  // D119.19: a line billed 0 shows the % its unit price makes over its unit cost = this same owner at quantity 1.
  const markupPercent: number | null =
    qty === 0
      ? priceClientInvoiceLine({ quantity: 1, quoteUnitPrice, actualUnitCost }).markupPercent
      : markupAmount == null || actualLineCost === 0 ? null : round2((markupAmount / actualLineCost!) * 100);
  const varianceAmount = quoted == null || actualLineCost == null ? null : round2(quoted - actualLineCost);
  const variancePercent = varianceAmount == null || quoted === 0 ? null : round2((varianceAmount / quoted!) * 100);

  return { invoiceUnitPrice, invoiceLineTotal, actualLineCost, markupAmount, markupPercent, varianceAmount, variancePercent };
}

/**
 * D119.15 - the mark-up % a line is meant to be priced at, judged at read time. `typedMarkupPercent` is
 * the rate a person typed (or the order quoted); the line keeps it whatever happens to its cost or price,
 * and it applies only while `sellingPriceAt(cost, typed) === price`. Otherwise the ACTUAL realised % stands.
 * Null: no cost or no price to judge against (the caller decides what a bare typed rate means).
 */
export function lineMarkup(
  line?: { actualUnitCost?: any; quoteUnitPrice?: any; typedMarkupPercent?: any } | null
): { percent: number; basis: 'typed' | 'actual' } | null {
  const cost = num(line?.actualUnitCost);
  const price = num(line?.quoteUnitPrice);
  if (cost === null || price === null) return null;
  const typed = num(line?.typedMarkupPercent);
  if (typed !== null && sellingPriceAt(cost, typed) === price) return { percent: typed, basis: 'typed' };
  const actual = realizedMarkupPercent(cost, price);
  return actual === null ? null : { percent: actual, basis: 'actual' };
}

// D157.9: the costBasis values a PERSON sets: 'manual' (typed) and 'chosen' (picked on the line).
// Every re-cost and an order change keep them; only "Use system cost" (null) clears them.
export const PERSON_COST_BASES: readonly string[] = Object.freeze(['manual', 'chosen']);
export function isPersonSetCost(basis: unknown): boolean {
  return PERSON_COST_BASES.includes(basis as string);
}

/** The line came from the confirmed order (a stored confirmedOrderItemIndex); a line staff added has none. */
export function isOrderDerivedLine(item: any): boolean {
  return num(item?.confirmedOrderItemIndex) !== null;
}

/** D98.5 / D170.7: Not supplied = an order line, named, ordered above 0, billed 0 or nothing. It stays on the invoice. */
export function isNotSuppliedLine(item: any): boolean {
  const ordered = num(item?.confirmedQuantity);
  const billed = billedQuantity(item);
  return (
    isOrderDerivedLine(item) &&
    String(item?.itemName || item?.itemDescription || '').trim() !== '' &&
    ordered !== null && ordered > 0 &&
    (billed === null || billed === 0)
  );
}

/** D170.7: a stored reason marks the line Not supplied; it bills and ships nothing until the reason is cleared. */
export function isMarkedNotSupplied(item: any): boolean {
  return String(item?.notSuppliedReason ?? '').trim() !== '';
}
